import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { deDateTime, deWeekdayDayMonth } from "../lib/dateFormat";

function Pill({ status }) {
  const map = {
    proposed: { label: "Vorgeschlagen", color: BRAND_COLORS.gold },
    accepted: { label: "Angenommen", color: BRAND_COLORS.navy },
    declined: { label: "Abgelehnt", color: STATUS_COLORS.red },
    checked_in: { label: "Vor Ort", color: BRAND_COLORS.navy },
    completed: { label: "Erledigt", color: BRAND_COLORS.greenDeep },
  };
  const s = map[status] || { label: status, color: BRAND_COLORS.navy };
  return (
    <span
      data-testid={`assignment-status-${status}`}
      className="inline-flex items-center gap-2 px-3 py-1 text-[10.5px] tracking-[0.2em] uppercase border"
      style={{ borderColor: `${s.color}40`, color: s.color }}
    >
      <span className="w-1.5 h-1.5" style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

export default function PartnerAssignments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const [complete, setComplete] = useState({ hours: "", partner_notes: "", photo_file_ids: [] });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/assignments");
      setRows(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/partner/assignments/${id}/status?status=${status}`);
      const label = status === "accepted" ? "Auftrag angenommen." : status === "declined" ? "Auftrag abgelehnt." : "Check-in erfasst.";
      toast.success(label);
      await load();
    } catch (e) { console.error("assignment status update failed:", e); toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setComplete((c) => ({ ...c, photo_file_ids: [...c.photo_file_ids, data.id] }));
    } catch (e) { toast.error("Upload fehlgeschlagen."); }
    finally { setUploading(false); }
  };

  const submitComplete = async (id) => {
    if (!complete.photo_file_ids.length) return toast.error("Mindestens ein Foto als Nachweis erforderlich.");
    try {
      await api.post(`/partner/assignments/${id}/complete`, {
        hours: complete.hours ? parseFloat(complete.hours) : null,
        partner_notes: complete.partner_notes || null,
        photo_file_ids: complete.photo_file_ids,
      });
      toast.success("Auftrag abgeschlossen.");
      setCompletingId(null);
      setComplete({ hours: "", partner_notes: "", photo_file_ids: [] });
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="partner-assignments">
      <div className="mb-10">
        <div className="label-caps mb-3">Partner-Portal</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Ihre Aufträge</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">Auftragsübersicht mit Annahme, Check-in vor Ort und Foto-Nachweis bei Abschluss.</p>
      </div>

      {loading ? (
        <div className="bg-white border border-navy/10 p-16 text-center label-caps">Wird geladen…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-navy/10 p-16 text-center">
          <div className="font-display text-2xl text-navy mb-2">Keine Aufträge</div>
          <p className="text-[13px] text-navy-dark/60">Sobald das Weissblom-Team einen Auftrag zuweist, erscheint er hier.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((a) => (
            <div key={a.id} data-testid={`assignment-row-${a.id}`} className="bg-white border border-navy/10 p-6 md:p-7">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="label-caps mb-2">{deWeekdayDayMonth(a.scheduled_date)} · {a.kind}</div>
                  <h3 className="font-display text-2xl text-navy leading-tight">{a.title}</h3>
                  <div className="text-[13px] text-navy-dark/70 mt-1">{a.property_name} — {a.property_address}</div>
                  {a.description && <p className="text-[13px] text-navy-dark/80 mt-3 leading-relaxed">{a.description}</p>}
                </div>
                <Pill status={a.status} />
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                {a.status === "proposed" && (
                  <>
                    <button data-testid={`accept-btn-${a.id}`} onClick={() => setStatus(a.id, "accepted")}
                      className="text-ivory px-5 py-2 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
                      style={{ background: BRAND_COLORS.navy }}>Annehmen</button>
                    <button data-testid={`decline-btn-${a.id}`} onClick={() => setStatus(a.id, "declined")}
                      className="border border-navy/30 text-navy px-5 py-2 text-[12px] tracking-[0.15em] uppercase hover:bg-navy/5">Ablehnen</button>
                  </>
                )}
                {a.status === "accepted" && (
                  <button data-testid={`checkin-btn-${a.id}`} onClick={() => setStatus(a.id, "checked_in")}
                    className="text-ivory px-5 py-2 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
                    style={{ background: BRAND_COLORS.navy }}>Check-in vor Ort</button>
                )}
                {a.status === "checked_in" && (
                  <button data-testid={`start-complete-btn-${a.id}`} onClick={() => { setCompletingId(a.id); setComplete({ hours: "", partner_notes: "", photo_file_ids: [] }); }}
                    className="text-ivory px-5 py-2 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
                    style={{ background: BRAND_COLORS.navy }}>Abschließen (Fotos + Stunden)</button>
                )}
                {a.status === "completed" && a.completed_at && (
                  <div className="text-[12px] text-navy-dark/60">Abgeschlossen am {deDateTime(a.completed_at)} · {a.hours ?? "—"} h · {a.photo_file_ids?.length || 0} Foto(s)</div>
                )}
              </div>

              {completingId === a.id && (
                <div className="mt-6 border-t border-navy/10 pt-6" data-testid={`complete-form-${a.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input data-testid="hours-input" type="number" step="0.25" min="0" placeholder="Stunden vor Ort"
                      value={complete.hours} onChange={(e) => setComplete({ ...complete, hours: e.target.value })}
                      className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
                    <label className="inline-flex items-center gap-3 text-[13px] text-navy-dark cursor-pointer border border-navy/20 px-4 py-3 hover:border-gold">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                      <span data-testid="upload-photo-btn">📷 Foto hochladen {uploading && "…"}</span>
                    </label>
                  </div>
                  <textarea rows={3} placeholder="Notizen für das Team (optional)"
                    value={complete.partner_notes} onChange={(e) => setComplete({ ...complete, partner_notes: e.target.value })}
                    className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 mb-4" />
                  {complete.photo_file_ids.length > 0 && (
                    <div className="mb-4 text-[12px] text-navy-dark/60">Fotos hochgeladen: {complete.photo_file_ids.length}</div>
                  )}
                  <div className="flex gap-3">
                    <button data-testid={`submit-complete-btn-${a.id}`} onClick={() => submitComplete(a.id)}
                      className="text-ivory px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
                      style={{ background: BRAND_COLORS.navy }}>Auftrag abschließen</button>
                    <button onClick={() => setCompletingId(null)}
                      className="text-[12px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy">Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
